import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, INK, OK, STROKE, TYPE, alpha, clamp, easierNumbers, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { ThingSvg, ease, mix, paperOf, pick, pop, shuffle } from './things.js';
import type { ThingId } from './things.js';

/**
 * ARCHIVIST · FIND THE PAIRS — turn two cards at a time and find the pairs.
 *
 * The classic memory game, on archive cards with drawn pictures: the last photos of Earth at the
 * first stop, spare parts under the sand. Tap a card and it turns over; tap a second. Two the same
 * stay up, ticked; two different turn back after a moment. Turning over two strangers is how it
 * is played; the mistake is turning over a card you had already seen and not matching it, or
 * passing by the twin you had already seen (as in the classic RECALL). Rounds 1 and 2 have three
 * pairs, round 3 four, and the cards turn back a little sooner each round.
 *
 * On the kit (kit.tsx): the cards lie in the board's own 320 × 320 space and turn over in its
 * SVG. The view state (`PairsView`) holds how far each card has turned and the picture of each
 * card that shows, never the face of a card lying face down.
 */

/** Easier since 6 Oct 2026 (kit `easierNumbers`): round 1 as before, round 2 halfway, round 3 the old round 2. */
export const PAIRS: readonly number[] = easierNumbers([3, 4]);
/** How long two unmatched cards stay face up. */
export const FLIP_BACK_MS: readonly number[] = easierNumbers([1_150, 1_000]);
/** A pair shows this long before it is ticked. */
const MATCH_MS = 380;
/** A card turns over in this long. */
const FLIP_MS = 340;
/** A pair's tick comes on over this. */
const TICK_MS = 260;

/** What each era's archive holds: photos of Earth, city things, wreckage, spare parts, the garden. */
export const DECKS: Record<Era, readonly ThingId[]> = {
  stars: ['tree', 'house', 'boat', 'fish', 'flower', 'bird', 'umbrella', 'apple'],
  neon: ['camera', 'radio', 'helmet', 'lamp', 'umbrella', 'clock', 'mug', 'bottle'],
  debris: ['helmet', 'boot', 'lantern', 'anchor', 'radio', 'wrench', 'camera', 'key'],
  dust: ['cog', 'bolt', 'spring', 'bulb', 'valve', 'battery', 'wrench', 'key'],
  canopy: ['tree', 'flower', 'can', 'fish', 'apple', 'teapot', 'bird', 'house'],
};

/** The cards of one round: `pairs` different things, each twice, shuffled. */
export function dealPairs(era: Era, pairs: number, rand: () => number): ThingId[] {
  const things = pick(DECKS[era], pairs, rand);
  return shuffle([...things, ...things], rand);
}

/**
 * Two cards turned over: a pair, a fair miss, or the mistake — the guest could have known better,
 * because they had already seen the second card, or the first card's twin. `seen` is every card
 * that had been face up before the second one was turned.
 */
export function pairsVerdict(deck: readonly ThingId[], first: number, second: number, seen: ReadonlySet<number>): 'match' | 'miss' | 'mistake' {
  if (deck[first] === deck[second]) return 'match';
  const twinSeen = deck.some((t, j) => j !== first && t === deck[first] && seen.has(j));
  return twinSeen || seen.has(second) ? 'mistake' : 'miss';
}

const PAD = 12;
const GAP = 10;

/**
 * Where card `i` of `n` lies on the board, in units: six as 3 + 3, eight as 4 + 4, ten as
 * 4 + 4 + 2, the rows in the middle. Never narrower than a finger and a half.
 */
export function cardBox(i: number, n: number): { x: number; y: number; w: number; h: number } {
  const perRow = Math.min(4, Math.max(1, Math.ceil(n / 2)));
  const rows = Math.ceil(n / perRow);
  const w = (BOARD.w - 2 * PAD - (perRow - 1) * GAP) / perRow;
  const h = Math.min((BOARD.h - 2 * PAD - (rows - 1) * GAP) / rows, w * 1.5);
  const top = (BOARD.h - rows * h - (rows - 1) * GAP) / 2;
  const row = Math.floor(i / perRow);
  const inRow = Math.min(perRow, n - row * perRow);
  const left = (BOARD.w - inRow * w - (inRow - 1) * GAP) / 2;
  return { x: left + (i - row * perRow) * (w + GAP), y: top + row * (h + GAP), w, h };
}

/** The card under a finger at `p`, or −1 (a touch in the gap goes to the nearer card). */
export function cardAt(p: Pt, n: number): number {
  for (let i = 0; i < n; i++) {
    const b = cardBox(i, n);
    if (p.x >= b.x - GAP / 2 && p.x <= b.x + b.w + GAP / 2 && p.y >= b.y - GAP / 2 && p.y <= b.y + b.h + GAP / 2) return i;
  }
  return -1;
}

/** Everything the table of cards shows. */
export interface PairsView {
  /** The game clock, ms. */
  t: number;
  /** Each card's picture (an index into the era's deck) while it is up or turning; −1 while it lies face down. */
  k: readonly number[];
  /** How far each card has turned over: 0 face down, 1 face up. */
  u: readonly number[];
  /** Each card's tick, 0–1, once it is one of a pair found. */
  m: readonly number[];
  /** 0 playing, 1 the last two were a mistake (until the next card), 2 every pair found. */
  s: 0 | 1 | 2;
  fx?: Fx | undefined;
}

/** The back of an archive card: a sleeve with the archive's seal, an open book. */
function CardBack({ w, h, era, id }: { w: number; h: number; era: Era; id: string }) {
  const skin = ERA_SKIN[era];
  const seal = Math.min(58, Math.min(w, h) * 0.62);
  return (
    <g>
      <rect width={w} height={h} rx="9" fill={`url(#${id}back)`} stroke={alpha(skin.ink, 0.53)} strokeWidth="1.5" />
      <rect x="3.5" y="3.5" width={w - 7} height={h - 7} rx="6" fill="none" stroke={alpha(skin.ink, 0.2)} />
      <g transform={`translate(${w / 2 - seal / 2} ${h / 2 - seal / 2}) scale(${seal / 40})`}>
        <circle cx="20" cy="20" r="16" fill="none" stroke={skin.ink} strokeWidth="1.4" opacity=".8" />
        <circle cx="20" cy="20" r="12.5" fill="none" stroke={skin.haze} strokeWidth=".9" strokeDasharray="1.6 1.6" opacity=".6" />
        <path d="M11 15.5C14 14 17 14.2 20 16C23 14.2 26 14 29 15.5V26C26 24.6 23 24.8 20 26.5C17 24.8 14 24.6 11 26Z" fill={alpha(skin.ink, 0.2)} stroke={skin.haze} strokeWidth="1.1" strokeLinejoin="round" />
        <path d="M20 16V26.5" stroke={skin.haze} strokeWidth="1" />
      </g>
    </g>
  );
}

/** The face: a photo on white card at the first stop, a record card with its era's tint elsewhere. */
function CardFace({ w, h, era, thing, tick, id }: { w: number; h: number; era: Era; thing: ThingId; tick: number; id: string }) {
  const paper = paperOf(era);
  const photo = era === 'stars';
  const inset = photo ? { l: 5, t: 5, r: 5, b: 14 } : { l: 4, t: 4, r: 4, b: 4 };
  const iw = w - inset.l - inset.r;
  const ih = h - inset.t - inset.b;
  const size = Math.min(84, Math.min(iw, ih) * 0.92);
  const done = tick > 0;
  const badge = pop(tick);
  return (
    <g>
      {done && <rect x="-3" y="-3" width={w + 6} height={h + 6} rx="11" fill="none" stroke={OK} strokeWidth="5" opacity={0.35 * tick} />}
      <rect width={w} height={h} rx="9" fill={photo ? '#f5f1e6' : `url(#${id}paper)`} stroke={done ? OK : paper.edge} strokeWidth={STROKE.line} />
      <rect x={inset.l} y={inset.t} width={iw} height={ih} rx="4" fill={photo ? `url(#${id}photo)` : `url(#${id}shine)`} />
      <ThingSvg thing={thing} x={w / 2 - size / 2} y={inset.t + ih / 2 - size / 2} width={size} height={size} />
      {done && (
        <g transform={`translate(${w - 12} 12) scale(${badge.scale})`} opacity={badge.opacity}>
          <circle r="9" fill={OK} />
          <text y={TYPE.hint * 0.36} textAnchor="middle" fontFamily={mono} fontSize={TYPE.hint} fontWeight="700" fill={INK.onFill}>
            ✓
          </text>
        </g>
      )}
    </g>
  );
}

/** The cards, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<PairsView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const paper = paperOf(era);
  const n = view.k.length;
  const deck = DECKS[era];
  // A demo glides every number between its keys: a pair counts once its tick is half on.
  const found = Math.floor(view.m.filter((v) => v >= 0.5).length / 2);
  const status =
    view.s === 2
      ? { text: say(lang, '✓ ALL PAIRS', '✓ ALLE PAR'), tone: 'ok' as const }
      : view.s === 1
        ? { text: say(lang, 'YOU HAD SEEN THAT ONE', 'DEN HADDE DU SETT'), tone: 'bad' as const }
        : { text: say(lang, 'PAIRS', 'PAR'), count: [found, n / 2] as const, hint: say(lang, 'TURN TWO CARDS', 'SNU TO KORT') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}back`} x1="0" y1="0" x2=".5" y2="1">
          <stop offset="0" stopColor={mix(skin.bg, skin.ink, 0.32)} />
          <stop offset=".7" stopColor={mix(skin.bg, '#000000', 0.2)} />
        </linearGradient>
        <linearGradient id={`${id}paper`} x1="0" y1="0" x2=".3" y2="1">
          <stop offset="0" stopColor={paper.top} />
          <stop offset="1" stopColor={paper.bottom} />
        </linearGradient>
        <linearGradient id={`${id}photo`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#8fc6f2" />
          <stop offset=".62" stopColor="#d8ecfa" />
          <stop offset=".62" stopColor="#b9d99a" />
          <stop offset="1" stopColor="#93bf72" />
        </linearGradient>
        <radialGradient id={`${id}shine`} cx=".5" cy=".4" r=".6">
          <stop offset="0" stopColor="#ffffff" stopOpacity=".4" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
      {view.k.map((k, i) => {
        const b = cardBox(i, n);
        const turn = ease(view.u[i] ?? 0);
        const up = turn > 0.5;
        // Turning over: the card narrows to an edge and widens again with its other side showing, lifted a little on the way.
        const sx = Math.max(0.02, Math.abs(Math.cos(turn * Math.PI)));
        const lift = 1 + 0.06 * Math.sin(turn * Math.PI);
        const thing = deck[Math.round(k)];
        return (
          <g key={i} transform={`translate(${q(b.x + b.w / 2, 1)} ${q(b.y + b.h / 2, 1)}) scale(${q(sx * lift, 3)} ${q(lift, 3)}) translate(${q(-b.w / 2, 1)} ${q(-b.h / 2, 1)})`}>
            <rect x="1" y="4" width={b.w} height={b.h} rx="9" fill="#000000" opacity=".4" />
            {up && thing ? (
              <CardFace w={b.w} h={b.h} era={era} thing={thing} tick={clamp(view.m[i] ?? 0, 0, 1)} id={id} />
            ) : (
              <CardBack w={b.w} h={b.h} era={era} id={id} />
            )}
          </g>
        );
      })}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, era, lang } = props;
  const [deck] = useState(() => dealPairs(era, PAIRS[level]!, seeded(Date.now() + level * 7919)));
  /** Each card: face up or not, and when it last turned (game clock). */
  const cards = useRef(deck.map(() => ({ up: false, at: -1e9 })));
  /** The cards up now and not yet a pair: none, one or two. */
  const open = useRef<number[]>([]);
  /** The pairs found, and when each card's tick came. */
  const matched = useRef(new Map<number, number>());
  /** Every card that has been face up. */
  const seen = useRef(new Set<number>());
  /** What happens next on the game clock: the pair ticked, or the two turned back. */
  const due = useRef<{ at: number; kind: 'match' | 'back' } | null>(null);
  /** The last two were a mistake: the status says so until the next card. */
  const note = useRef(false);

  const round = useRound(props, (_dt, now) => {
    const d = due.current;
    if (!d || now < d.at) return;
    due.current = null;
    if (d.kind === 'match') {
      for (const i of open.current) matched.current.set(i, now);
      open.current = [];
      round.hit();
      round.progress(matched.current.size / deck.length);
      if (matched.current.size >= deck.length) round.win();
      return;
    }
    for (const i of open.current) cards.current[i] = { up: false, at: now };
    open.current = [];
  });

  const tap = (p: Pt) => {
    if (!round.live || due.current) return;
    const i = cardAt(p, deck.length);
    if (i < 0 || matched.current.has(i) || open.current.includes(i)) return;
    const now = round.time();
    const before = new Set(seen.current);
    seen.current.add(i);
    cards.current[i] = { up: true, at: now };
    const first = open.current[0];
    if (first === undefined) {
      open.current = [i];
      note.current = false;
      return;
    }
    open.current = [first, i];
    const verdict = pairsVerdict(deck, first, i, before);
    if (verdict === 'match') {
      due.current = { at: now + MATCH_MS, kind: 'match' };
      return;
    }
    if (verdict === 'mistake') {
      note.current = true;
      round.miss();
    }
    due.current = { at: now + FLIP_BACK_MS[level]!, kind: 'back' };
  };

  const now = round.now;
  const things = DECKS[era];
  const view: PairsView = {
    t: q(now),
    k: deck.map((thing, i) => {
      const c = cards.current[i]!;
      return c.up || now - c.at < FLIP_MS ? things.indexOf(thing) : -1;
    }),
    u: cards.current.map((c) => {
      const f = clamp((now - c.at) / FLIP_MS, 0, 1);
      return q(c.up ? f : 1 - f, 2);
    }),
    m: deck.map((_, i) => {
      const at = matched.current.get(i);
      return at === undefined ? 0 : q(clamp((now - at) / TICK_MS, 0, 1), 2);
    }),
    s: round.won ? 2 : note.current ? 1 : 0,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap a card to turn it over', 'Trykk på et kort for å snu det'), down: tap }}
    />
  );
}

/**
 * The how-to, on round 1's six cards: the finger turns two different cards and they turn back;
 * then it turns a new card, remembers where its twin was, turns that one too, and the pair stays
 * up, ticked.
 */
function demoTable(): DemoScript<PairsView> {
  const n = 6;
  // The pictures as they lie: cards 0 and 4 are a pair, card 1 another thing.
  const k = [0, 1, 2, 1, 0, 2];
  const centre = (i: number) => {
    const b = cardBox(i, n);
    return { x: q(b.x + b.w / 2), y: q(b.y + b.h / 2) };
  };
  const u = [0, 0, 0, 0, 0, 0];
  const m = [0, 0, 0, 0, 0, 0];
  const keys: DemoKey<PairsView>[] = [{ at: 0, view: { t: 0, k, u: [...u], m: [...m], s: 0 } }];
  const finger: FingerKey[] = [
    { at: 0, x: 262, y: 300, hide: true },
    { at: 300, x: 262, y: 300, hide: false },
  ];
  /** The finger taps card `i` at `at`, and it turns over. */
  const turn = (i: number, at: number, to: 0 | 1) => {
    keys.push({ at, view: { u: [...u] } });
    u[i] = to;
    keys.push({ at: at + FLIP_MS, view: { u: [...u] } });
  };
  const press = (i: number, at: number) => {
    const c = centre(i);
    finger.push({ at: at - 400, x: c.x, y: c.y }, { at, x: c.x, y: c.y, down: true }, { at: at + 150, x: c.x, y: c.y, down: false });
    turn(i, at, 1);
  };
  press(0, 900);
  press(1, 1_700);
  // Two different things: they turn back.
  keys.push({ at: 3_000, view: { u: [...u] } });
  u[0] = 0;
  u[1] = 0;
  keys.push({ at: 3_000 + FLIP_MS, view: { u: [...u] } });
  press(4, 4_000);
  press(0, 4_800);
  // The twin was remembered: a pair, ticked.
  keys.push({ at: 4_800 + MATCH_MS, view: { m: [...m], fx: [1, 0] }, ease: 'step' });
  m[0] = 1;
  m[4] = 1;
  keys.push({ at: 4_800 + MATCH_MS + TICK_MS, view: { m: [...m] } });
  keys.push({ at: 5_700, view: { fx: [0, 0] } });
  finger.push({ at: 5_700, x: 262, y: 300 }, { at: 5_900, x: 262, y: 300, hide: true });
  return {
    ms: 7_000,
    keys,
    finger,
    steps: [
      { at: 0, en: 'TAP TWO CARDS TO TURN THEM', no: 'TRYKK PÅ TO KORT FOR Å SNU' },
      { at: 2_200, en: 'NOT THE SAME? THEY TURN BACK', no: 'ULIKE? DE SNUR SEG TILBAKE' },
      { at: 3_700, en: 'TWO THE SAME STAY UP', no: 'TO LIKE BLIR LIGGENDE' },
    ],
  };
}

export const game = thirtyGame<PairsView>({
  Board,
  View,
  demo: demoTable(),
  words: {
    en: {
      instruction: 'Turn two cards at a time and find the pairs. Remember what you saw.',
      howMove: 'Tap a card to turn it over, then tap another. Two the same stay up.',
    },
    no: {
      instruction: 'Snu to kort om gangen og finn parene. Husk hva du har sett.',
      howMove: 'Trykk på et kort for å snu det, så på et til. To like blir liggende.',
    },
  },
});
